import React from 'react';
import { Stack, Box, Typography } from '@mui/material';
import { SalonDashboardReport, reportApi } from '../../../services/reportService';
import { StatTile } from '../../../components/ui';
import { tokens } from '../../../theme';
import { DateRange } from './StatsDateRange';
import {
  SectionCard,
  CHART_COLORS,
  formatNumber,
  formatPct,
  useAsyncData,
  pickGranularity,
  EmptyHint,
  StatTileGrid,
  StatsRow,
} from './statsShared';
import { GroupedBarChart, DonutChart } from './ChartKit';

interface Props {
  salonId: string;
  range: DateRange;
  data: SalonDashboardReport;
}

// Kolory statusow znacza stan: zielony — odbyla sie, sliwka — zaplanowana, bursztyn — odwolana, czerwony — klient nie przyszedl.
const STATUS_COLORS = {
  completed: tokens.success,
  pending: tokens.plumLight,
  cancelled: tokens.warning,
  noShow: tokens.danger,
};

const StatusBar: React.FC<{ label: string; value: number; total: number; color: string }> = ({ label, value, total, color }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1.5} mb={0.75}>
        <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.9375rem', color: tokens.ink }}>{label}</Typography>
        </Stack>
        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {formatNumber(value)}
          <Box component="span" sx={{ color: tokens.muted, fontWeight: 500, fontSize: '0.8125rem', ml: 0.75 }}>
            {formatPct(pct)}
          </Box>
        </Typography>
      </Stack>
      <Box sx={{ height: 8, borderRadius: 99, bgcolor: tokens.surfaceStrong, overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: color, borderRadius: 99 }} />
      </Box>
    </Box>
  );
};

export const BookingsTab: React.FC<Props> = ({ salonId, range, data }) => {
  const granularity = pickGranularity(range.dateFrom, range.dateTo);
  const { data: ts } = useAsyncData(
    () => reportApi.getTimeseries(salonId, range.dateFrom, range.dateTo, granularity),
    [salonId, range.dateFrom, range.dateTo, granularity],
  );

  const b = data.bookings;
  const donut = [
    { name: 'Zakończone', value: b.completed, color: STATUS_COLORS.completed },
    { name: 'Oczekujące', value: b.pending, color: STATUS_COLORS.pending },
    { name: 'Anulowane', value: b.cancelled, color: STATUS_COLORS.cancelled },
    { name: 'Nieobecność klienta', value: b.noShow, color: STATUS_COLORS.noShow },
  ];

  return (
    <Stack gap={2}>
      <StatTileGrid>
        <StatTile label="Wszystkie wizyty" value={formatNumber(b.total)} />
        <StatTile label="Zakończone" value={formatNumber(b.completed)} />
        <StatTile label="Anulowane" value={formatNumber(b.cancelled)} />
        <StatTile label="Średnio dziennie" value={formatNumber(b.avgPerDay)} />
      </StatTileGrid>

      <StatsRow template="5fr 7fr">
        <SectionCard title="Statusy wizyt">
          {b.total > 0 ? <DonutChart data={donut} currency={false} /> : <EmptyHint>Brak wizyt w wybranym okresie</EmptyHint>}
        </SectionCard>
        <SectionCard title="Rozkład statusów" subtitle="Udział w liczbie wszystkich wizyt">
          <Stack gap={2.25} mt={0.5}>
            <StatusBar label="Zakończone" value={b.completed} total={b.total} color={STATUS_COLORS.completed} />
            <StatusBar label="Oczekujące i potwierdzone" value={b.pending} total={b.total} color={STATUS_COLORS.pending} />
            <StatusBar label="Anulowane" value={b.cancelled} total={b.total} color={STATUS_COLORS.cancelled} />
            <StatusBar label="Nieobecność klienta" value={b.noShow} total={b.total} color={STATUS_COLORS.noShow} />
          </Stack>
        </SectionCard>
      </StatsRow>

      <SectionCard title="Liczba wizyt w czasie" subtitle={granularity === 'week' ? 'Sumy tygodniowe' : 'Sumy dzienne'}>
        {ts && ts.buckets.some((x) => x.bookings > 0) ? (
          <GroupedBarChart
            data={ts.buckets as unknown as Record<string, unknown>[]}
            xKey="label"
            bars={[{ key: 'bookings', name: 'Wizyty', color: CHART_COLORS[0] }]}
          />
        ) : (
          <EmptyHint description="Wybierz dłuższy okres albo inny zakres dat.">Brak wizyt w wybranym okresie</EmptyHint>
        )}
      </SectionCard>
    </Stack>
  );
};
